/**
 * Agent entitlement: where the tenant stands, and the route to more.
 *
 * WHY THIS PAGE EXISTS SEPARATELY FROM EDGE FLEET
 *
 * Edge Fleet answers "what is running and is it healthy" and shows a banner
 * when the tenant is over. That banner is a warning, not a workflow - it tells
 * an operator there is a problem and gives them nowhere to go. This page is the
 * somewhere: the ceiling per class, the live count against it, and the request
 * that gets it raised.
 *
 * WHAT AN OPERATOR CAN AND CANNOT DO HERE
 *
 * The ceiling is a FOCTTA commercial control (`entitlement.limits.set` is held
 * at platform_super_admin), so a tenant admin sees it and cannot edit it. That
 * is deliberate and it is shown rather than hidden: rendering an editable field
 * the server will refuse is worse than rendering a read-only one with the
 * reason next to it.
 *
 * The decide controls only appear for a role that may actually decide. A
 * disabled button an operator cannot explain is a support ticket.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, Clock, Send, ShieldCheck, XCircle } from 'lucide-react';

import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { KpiCard } from '@/components/viz/KpiCard';
import { ErrorState, LoadingState } from '@foctta/ui-shell';
import {
  createAgentIncreaseRequest,
  decideAgentIncreaseRequest,
  getAgentLimits,
  listAgentIncreaseRequests,
  getAuthUser,
  type AgentIncreaseRequest,
  type AgentEntitlementVerdict,
} from '@/lib/api';

type AgentClass = 'endpoint' | 'vpcPod' | 'gateway';

const CLASS_LABEL: Record<AgentClass, string> = {
  endpoint: 'Endpoint agents',
  vpcPod: 'VPC scanner pods',
  gateway: 'Detection gateways',
};

/** Only these roles see the decide controls. Mirrors control-authority.ts. */
const APPROVER_ROLES = new Set(['platform_super_admin']);

function stateTone(state: string): { cls: string; label: string } {
  switch (state) {
    case 'over':
      return { cls: 'text-red-400 border-red-500/40 bg-red-500/10', label: 'Over entitlement' };
    case 'grace':
      return { cls: 'text-amber-400 border-amber-500/40 bg-amber-500/10', label: 'In grace' };
    case 'unlimited':
      return { cls: 'text-slate-400 border-slate-500/40 bg-slate-500/10', label: 'No ceiling set' };
    default:
      return { cls: 'text-emerald-400 border-emerald-500/40 bg-emerald-500/10', label: 'Within' };
  }
}

function requestTone(state: string): string {
  if (state === 'approved') return 'text-emerald-400';
  if (state === 'rejected') return 'text-red-400';
  if (state === 'withdrawn') return 'text-slate-400';
  return 'text-amber-400';
}

export function AdminAgentEntitlement() {
  const [limits, setLimits] = useState<Awaited<ReturnType<typeof getAgentLimits>> | null>(null);
  const [requests, setRequests] = useState<AgentIncreaseRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const [form, setForm] = useState<{ agentClass: AgentClass; requestedLimit: string; justification: string }>({
    agentClass: 'endpoint',
    requestedLimit: '',
    justification: '',
  });

  const actorRole = getAuthUser()?.role ?? '';
  const mayDecide = APPROVER_ROLES.has(actorRole);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [l, r] = await Promise.all([getAgentLimits(), listAgentIncreaseRequests()]);
      setLimits(l);
      setRequests(r);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to load entitlement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // An open request per class blocks a second one (partial unique index), so
  // the form says so up front rather than letting the operator write a
  // justification and then collect a 409.
  const openClasses = useMemo(
    () => new Set(requests.filter((r) => r.state === 'requested').map((r) => r.agentClass)),
    [requests],
  );

  async function submit() {
    const n = Number(form.requestedLimit);
    if (!Number.isInteger(n) || n <= 0) {
      setNotice('Requested limit must be a whole number greater than zero.');
      return;
    }
    if (form.justification.trim().length < 10) {
      setNotice('A justification of at least 10 characters is required - it is the part anyone reading this a year from now actually needs.');
      return;
    }
    setBusy(true);
    setNotice(null);
    try {
      const created = await createAgentIncreaseRequest({
        agentClass: form.agentClass,
        requestedLimit: n,
        justification: form.justification.trim(),
      });
      // Say what actually happened upstream rather than implying delivery.
      setNotice(
        created.upstream.delivered
          ? `Request raised and sent to ${created.upstream.kind}.`
          : 'Request recorded. It has not yet reached FOCTTA - it will be picked up from the record.',
      );
      setForm({ agentClass: form.agentClass, requestedLimit: '', justification: '' });
      await load();
    } catch (e) {
      setNotice(e instanceof Error ? e.message : 'could not raise the request');
    } finally {
      setBusy(false);
    }
  }

  async function decide(id: string, decision: 'approved' | 'rejected', applyLimit: boolean) {
    setBusy(true);
    setNotice(null);
    try {
      const out = await decideAgentIncreaseRequest(id, { decision, applyLimit });
      setNotice(
        out.ceilingApplied !== null
          ? `Approved, and the ceiling is now ${out.ceilingApplied}.`
          : decision === 'approved'
            ? 'Approved. The ceiling is unchanged until it is applied.'
            : 'Rejected.',
      );
      await load();
    } catch (e) {
      setNotice(e instanceof Error ? e.message : 'could not record the decision');
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <LoadingState />;
  if (error) return <ErrorState title="Failed to load entitlement" detail={error} onRetry={() => void load()} />;

  return (
    <PageContainer>
      <PageHeader
        title="Agent entitlement"
        subtitle="How many agents this tenant is licensed for, how many are live, and the route to raising it. The ceiling is set by FOCTTA; this page is where a tenant sees it and asks for more."
      />

      {notice !== null && (
        <div className="mb-4 rounded-lg border border-sky-500/40 bg-sky-500/10 px-4 py-3 text-sm text-sky-200">
          {notice}
        </div>
      )}

      {/* Per class: the ceiling, the live count, and the verdict. */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {limits?.classes.map(({ agentClass, verdict }: { agentClass: string; verdict: AgentEntitlementVerdict }) => {
          const tone = stateTone(verdict.state);
          return (
            <KpiCard
              key={agentClass}
              label={CLASS_LABEL[agentClass as AgentClass] ?? agentClass}
              // The LIVE count is the number; the ceiling goes in the hint. A
              // KpiCard value is numeric, and "12 / 10" as a string would also
              // read as a fraction rather than a count against a limit.
              value={verdict.live}
              glow={verdict.state === 'over' ? 'critical' : verdict.state === 'grace' ? 'medium' : 'low'}
              hint={
                verdict.limit === null
                  ? 'no ceiling set - nothing enforced'
                  : `of ${verdict.limit} licensed · ${tone.label.toLowerCase()}`
              }
            />
          );
        })}
      </div>

      {/* The verdict in words. A number alone does not tell an operator whether
          they are about to be blocked, or what happens if they are. */}
      <div className="mt-4 grid gap-3">
        {limits?.classes
          .filter(({ verdict }: { verdict: AgentEntitlementVerdict }) => verdict.state !== 'within')
          .map(({ agentClass, verdict }: { agentClass: string; verdict: AgentEntitlementVerdict }) => {
            const tone = stateTone(verdict.state);
            return (
              <div key={agentClass} className={`rounded-lg border px-4 py-3 text-sm ${tone.cls}`}>
                <div className="flex items-center gap-2 font-medium">
                  {verdict.state === 'over' ? (
                    <XCircle className="h-4 w-4" />
                  ) : verdict.state === 'grace' ? (
                    <AlertTriangle className="h-4 w-4" />
                  ) : (
                    <ShieldCheck className="h-4 w-4" />
                  )}
                  {CLASS_LABEL[agentClass as AgentClass] ?? agentClass} · {tone.label}
                </div>
                <p className="mt-1 opacity-90">{verdict.summary}</p>
              </div>
            );
          })}
      </div>

      {/* Raise a request. */}
      <GlassCard className="mt-6">
        <CardHeader>
          <CardTitle>Request a higher ceiling</CardTitle>
          <CardDescription>
            The ceiling is set by FOCTTA, not here — {limits?.approvalSource === 'local'
              ? 'requests are recorded and reviewed directly.'
              : `requests are sent to ${limits?.approvalSource}.`}{' '}
            Existing agents keep running and new devices keep enrolling while a request is open.
          </CardDescription>
        </CardHeader>
        <div className="grid gap-3 px-6 pb-6 sm:grid-cols-[200px_160px_1fr_auto]">
          <select
            aria-label="Agent class"
            className="rounded-md border border-white/10 bg-slate-900/60 px-3 py-2 text-sm"
            value={form.agentClass}
            onChange={(e) => setForm({ ...form, agentClass: e.target.value as AgentClass })}
          >
            {(Object.keys(CLASS_LABEL) as AgentClass[]).map((c) => (
              <option key={c} value={c} disabled={openClasses.has(c)}>
                {CLASS_LABEL[c]}
                {openClasses.has(c) ? ' — request already open' : ''}
              </option>
            ))}
          </select>
          <input
            aria-label="Requested limit"
            inputMode="numeric"
            placeholder="New limit"
            className="rounded-md border border-white/10 bg-slate-900/60 px-3 py-2 text-sm"
            value={form.requestedLimit}
            onChange={(e) => setForm({ ...form, requestedLimit: e.target.value })}
          />
          <input
            aria-label="Business justification"
            placeholder="Why — this is the part anyone reading it later actually needs"
            className="rounded-md border border-white/10 bg-slate-900/60 px-3 py-2 text-sm"
            value={form.justification}
            onChange={(e) => setForm({ ...form, justification: e.target.value })}
          />
          <Button onClick={() => void submit()} disabled={busy || openClasses.has(form.agentClass)}>
            <Send className="mr-2 h-4 w-4" />
            Raise
          </Button>
        </div>
      </GlassCard>

      {/* The paper trail. */}
      <GlassCard className="mt-6">
        <CardHeader>
          <CardTitle>Requests</CardTitle>
          <CardDescription>
            When each was raised, by whom, what was asked for, and who decided. This record is the
            deliverable — an email thread is not.
          </CardDescription>
        </CardHeader>
        <div className="px-6 pb-6">
          {requests.length === 0 ? (
            <p className="py-6 text-sm text-slate-400">No requests raised yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-slate-400">
                    <th className="py-2 pr-4">Raised</th>
                    <th className="py-2 pr-4">Class</th>
                    <th className="py-2 pr-4">At the time</th>
                    <th className="py-2 pr-4">Asked for</th>
                    <th className="py-2 pr-4">Justification</th>
                    <th className="py-2 pr-4">State</th>
                    {mayDecide && <th className="py-2">Decide</th>}
                  </tr>
                </thead>
                <tbody>
                  {requests.map((r) => (
                    <tr key={r.id} className="border-t border-white/5 align-top">
                      <td className="py-3 pr-4 whitespace-nowrap text-slate-300">
                        {new Date(r.createdAt).toLocaleDateString()}
                      </td>
                      <td className="py-3 pr-4">{CLASS_LABEL[r.agentClass] ?? r.agentClass}</td>
                      {/* Captured at request time so the row still reads correctly
                          after the ceiling changes. */}
                      <td className="py-3 pr-4 tabular-nums text-slate-300">
                        {r.liveAtRequest} live / {r.currentLimit ?? '—'}
                      </td>
                      <td className="py-3 pr-4 tabular-nums">{r.requestedLimit}</td>
                      <td className="py-3 pr-4 max-w-sm text-slate-300">{r.justification}</td>
                      <td className={`py-3 pr-4 font-medium ${requestTone(r.state)}`}>
                        <span className="inline-flex items-center gap-1.5">
                          {r.state === 'requested' ? (
                            <Clock className="h-3.5 w-3.5" />
                          ) : r.state === 'approved' ? (
                            <CheckCircle2 className="h-3.5 w-3.5" />
                          ) : (
                            <XCircle className="h-3.5 w-3.5" />
                          )}
                          {r.state}
                        </span>
                        {r.decidedBy !== null && (
                          <div className="mt-0.5 text-xs font-normal text-slate-400">
                            by {r.decidedBy}
                          </div>
                        )}
                      </td>
                      {mayDecide && (
                        <td className="py-3">
                          {r.state === 'requested' ? (
                            <div className="flex flex-wrap gap-2">
                              {/* Two approve buttons, because approving and moving
                                  the ceiling are different acts. The plain one
                                  records agreement; the other also raises it. */}
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={busy}
                                onClick={() => void decide(r.id, 'approved', false)}
                              >
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                disabled={busy}
                                onClick={() => void decide(r.id, 'approved', true)}
                              >
                                Approve + apply
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={busy}
                                onClick={() => void decide(r.id, 'rejected', false)}
                              >
                                Reject
                              </Button>
                            </div>
                          ) : (
                            <span className="text-xs text-slate-500">decided</span>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </GlassCard>

      <Badge className="mt-6" variant="outline">
        Ceiling managed by FOCTTA · approvals via {limits?.approvalSource ?? 'local'}
      </Badge>
    </PageContainer>
  );
}
